import {
  ApolloClientOptions,
  ApolloLink,
  InMemoryCache
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from 'apollo-angular/http';
import { NgModule, inject } from '@angular/core';
import { APOLLO_OPTIONS, provideApollo } from 'apollo-angular';
import { TELEMETRY_HTTP_CLIENT, TelemetryHttpClient } from '@rccl/telemetry-angular';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { API_PATH, APIGEE_APP_KEY_GQL, BRAND } from '~environments/environment';
import { SplunkService } from './shared/services/splunk.service';
import { getTelemetryErrorCodePerOperation } from './graphql-telemetry';
import { NetworkError } from '@apollo/client/errors';
import { GraphQLFormattedError } from 'graphql';
import { GraphQLErrorLookup } from './shared/types/endpoints-error-codes.type';

let splunkService: SplunkService;

const federationMap = {
  advertisement: ['MobileAppAdvertisementByPageId', 'MobileAppAdvertisementTracking'],
};

function getSubgraphHeaders(operation: any, subgraphs = federationMap) {
  for (const [gqlRouter, operations] of Object.entries(subgraphs)) {
    if (operations.includes(operation?.operationName)) {
      return {
        gqlRouter,
      };
    }
  }
  return {};
}

const headersLink = setContext((operation, { headers }) => {
  return {
    headers: {
      ...headers,
      ...getSubgraphHeaders(operation),
      appkey: APIGEE_APP_KEY_GQL,
    },
  };
});

const sendTelemetryErrorEvents = (
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
  networkError: NetworkError | undefined,
  operationName: string
) => {
  const errorCode = getTelemetryErrorCodePerOperation(operationName);
  const sendErrorEvent = (error: any) => {
    const context = splunkService.initializeContext('error', error);
    splunkService.errorByLookUp(errorCode as GraphQLErrorLookup, {
      context: context,
    });
  };
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      sendErrorEvent(error);
    }
  }
  if (networkError) {
    sendErrorEvent(networkError);
  }
};

const errorsLink = onError(/** NOSONAR */ ({ graphQLErrors, networkError, operation }) => {
  const operationName = operation?.operationName ?? '';
  sendTelemetryErrorEvents(graphQLErrors, networkError, operationName);
  if (graphQLErrors?.length) {
    console.warn('[GraphQL error%s]: %o', operationName, graphQLErrors);
  }
  if (networkError) {
    console.warn('[Network error%s]: %o', operationName, networkError);
  }
});

export function createApollo(): ApolloClientOptions<any> {
  splunkService = inject(SplunkService);
  const httpClient = inject<TelemetryHttpClient>(TELEMETRY_HTTP_CLIENT);
  const uri = `${API_PATH}/en/${BRAND}/web/graphql`;
  const customHttpLink = new HttpLink(httpClient).create({ uri });

  return {
    link: ApolloLink.from([headersLink, errorsLink, customHttpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  };
}

@NgModule({
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpClient],
    },
    provideApollo(() => {
      splunkService = inject(SplunkService);
      const httpClient = inject<TelemetryHttpClient>(TELEMETRY_HTTP_CLIENT);
      const uri = `${API_PATH}/en/${BRAND}/web/graphql`;
      const customHttpLink = new HttpLink(httpClient).create({ uri });
      return {
        link: ApolloLink.from([headersLink, errorsLink, customHttpLink]),
        cache: new InMemoryCache()
      };
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class GraphQLModule {}
